import { Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Regulation, type MarketDialogueDto } from "../../autogen/bff-api";
import { formatMoney } from "../../common/formatting/displaySum";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { TipTap } from "../../common/input/TipTap/TipTap";
import { serviceCategoryLabel } from "../../common/procurement/serviceCategory";
import { CpvCodeTags } from "../sourcing-events/buyer/wizard/components/summary/CpvCodeTags";
import { EmptyValue, SummaryField } from "../sourcing-events/buyer/wizard/components/summary/SummaryField";
import { SummarySection } from "../sourcing-events/buyer/wizard/components/summary/SummarySection";
import { MarketDialogueNoticeType } from "./MarketDialogueNoticeType";
import { MarketDialogueQuestionList } from "./MarketDialogueQuestions";
import { stepOfBlocker, type MarketDialogueStep } from "./marketDialogueSteps";

interface Props {
  dialogue: MarketDialogueDto;
  onEditStep?: (step: MarketDialogueStep) => void;
}

export const MarketDialogueSummary = ({ dialogue, onEditStep }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const notProvided = <EmptyValue text={t("Not set")} />;
  const sectionProps = (step: MarketDialogueStep) => ({
    editTarget: onEditStep ? { kind: "wizard-step" as const, step } : undefined,
    onWizardStepNavigate: onEditStep,
    missingCount: onEditStep ? dialogue.blockers.filter((b) => stepOfBlocker(b) === step).length : undefined,
  });
  const forsyning = dialogue.regulation === Regulation.Forsyningsforskriften;

  return (
    <Stack spacing="0" data-testid="market-dialogue-summary">
      <SummarySection title={t("Basis")} {...sectionProps("basis")}>
        <SummaryField label={t("Title")} value={dialogue.title || notProvided} />
        <SummaryField
          label={t("Description")}
          value={dialogue.description ? <TipTap content={dialogue.description} editable={false} maxHeight="240px" /> : notProvided}
        />
        {dialogue.canAnnounceOnDoffin && (
          <SummaryField label={t("Regulation")} value={dialogue.regulation ? t(dialogue.regulation) : notProvided} />
        )}
        {forsyning && (
          <SummaryField
            label={t("Utility activity")}
            value={dialogue.utilityActivity ? t(dialogue.utilityActivity) : notProvided}
          />
        )}
        <SummaryField
          label={t("What does the procurement concern?")}
          value={dialogue.contractNature ? t(dialogue.contractNature) : notProvided}
        />
        {dialogue.serviceCategory && (
          <SummaryField
            label={t("What kind of service is the contract?")}
            value={serviceCategoryLabel(t, dialogue.serviceCategory, forsyning)}
          />
        )}
        <SummaryField
          label={t("Estimated value")}
          value={dialogue.estimatedValueNok != null ? `${formatMoney(dialogue.estimatedValueNok)} NOK` : notProvided}
        />
        {dialogue.canAnnounceOnDoffin && (
          <SummaryField
            label={t("CPV codes")}
            value={dialogue.cpvCodes.length > 0 ? <CpvCodeTags codes={dialogue.cpvCodes} /> : notProvided}
          />
        )}
      </SummarySection>
      <SummarySection title={t("Questions")} {...sectionProps("questions")}>
        {dialogue.questions.length > 0 ? <MarketDialogueQuestionList questions={dialogue.questions} /> : notProvided}
      </SummarySection>
      {onEditStep && (
        <SummarySection title={t("Documents")} {...sectionProps("documents")}>
          {dialogue.documents.length > 0 ? (
            <Stack spacing="1">
              {dialogue.documents.map((document) => (
                <Text key={document.id} fontSize="sm">
                  {document.name}
                </Text>
              ))}
            </Stack>
          ) : (
            <EmptyValue text={t("No documents")} />
          )}
        </SummarySection>
      )}
      <SummarySection title={t("Reach the market")} {...sectionProps("reachMarket")}>
        <SummaryField
          label={t("Answer deadline")}
          value={dialogue.answerDeadline ? displayDateWithTime(dialogue.answerDeadline) : notProvided}
        />
        {dialogue.canAnnounceOnDoffin && (
          <SummaryField
            label={t("Expected announcement of the competition")}
            value={dialogue.expectedContractNoticeDate ? displayDate(dialogue.expectedContractNoticeDate) : notProvided}
          />
        )}
        {dialogue.announceOnDoffin && (
          <SummaryField label={t("Notice type")} value={<MarketDialogueNoticeType dialogue={dialogue} />} />
        )}
      </SummarySection>
    </Stack>
  );
};
